<template>
  <DSFormField
    :id="id"
    #default="{ describedBy }"
    :name="props.name"
    :label="props.label ?? undefined"
    :inline-edit="props.inlineEdit"
    :tooltip="props.tooltip"
    :hidden-label="props.hiddenLabel"
    :required="props.required"
    :disabled="props.disabled"
    :helper-text="helperText"
    :error-messages="dsFormField.errorMessages.value"
  >
    <div
      :class="rootClasses"
      data-cy="ds-password-field"
    >
      <DSBaseInput
        :id="id"
        ref="passwordInputRef"
        v-model="modelValue"
        :type="inputType"
        :name="props.name"
        :class="DS_PASSWORD_FIELD_CLASSES.Input"
        :data-cy-suffix="props.name"
        :placeholder="props.placeholder"
        :inline-edit="props.inlineEdit"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :required="props.required"
        :autocomplete="props.autocomplete"
        :maxlength="props.maxlength"
        :minlength="props.minlength"
        :clearable="isClearable"
        :has-error="hasErrors"
        :aria-invalid="hasErrors ? 'true' : undefined"
        :aria-describedby="describedBy"
        @update:model-value="onInput"
        @clear="onClear"
        @focus="onFocus"
        @blur="onBlur"
      >
        <template #suffix>
          <DSButton
            :class="DS_PASSWORD_FIELD_CLASSES.ToggleButton"
            data-cy="ds-password-field__toggle"
            :variant="DS_BUTTON_VARIANT.Ghost"
            :size="DS_SIZE.Small"
            :type="DS_BUTTON_TYPE.Button"
            :prefix-icon="toggleIcon"
            :aria-label="toggleAriaLabel"
            :disabled="props.disabled"
            :aria-pressed="String(isPasswordVisible)"
            @mousedown.prevent
            @click.stop.prevent="togglePasswordVisibility"
          />
        </template>
      </DSBaseInput>
    </div>
  </DSFormField>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { DS_BUTTON_TYPE, DS_BUTTON_VARIANT } from "@/src/design_system/button_and_controls/button/types/props";
import DSBaseInput from "@/src/design_system/form_elements/base_input/components/DSBaseInput.vue";
import { DS_BASE_INPUT_TYPE } from "@/src/design_system/form_elements/base_input/types/inputType";
import DSFormField from "@/src/design_system/form_elements/form_field/components/DSFormField.vue";
import { useDSPasswordFieldCapsLock } from "@/src/design_system/form_elements/password_field/composables/useDSPasswordFieldCapsLock";
import { DS_PASSWORD_FIELD_CLASSES } from "@/src/design_system/form_elements/password_field/constants/classes";
import type {
  DSPasswordFieldEmits,
  DSPasswordFieldModelValue,
  DSPasswordFieldProps,
} from "@/src/design_system/form_elements/password_field/types";
import { useDSFormField } from "@/src/design_system/form_elements/shared/composables/useDSFormField";
import { useDSFormFieldId } from "@/src/design_system/form_elements/shared/composables/useDSFormFieldId";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import { DS_STATE_CLASSES } from "@/src/design_system/shared/constants/stateClasses";
import { DS_SIZE } from "@/src/design_system/shared/types/size";

const PASSWORD_FIELD_I18N = window.I18n.designSystem.passwordField;
const CAPS_LOCK_WARNING_MESSAGE = PASSWORD_FIELD_I18N.capsLockWarning;
const SHOW_PASSWORD_LABEL = PASSWORD_FIELD_I18N.showPassword;
const HIDE_PASSWORD_LABEL = PASSWORD_FIELD_I18N.hidePassword;

const props = withDefaults(defineProps<DSPasswordFieldProps>(), {
  hiddenLabel: false,
  inlineEdit: false,
  disabled: false,
  readonly: false,
  required: false,
  tooltip: undefined,
  clearable: false,
  initiallyVisible: false,
  errorMessages: undefined,
  rules: undefined,
});

const modelValue = defineModel<DSPasswordFieldModelValue>({ default: "" });
const emit = defineEmits<DSPasswordFieldEmits>();

const passwordInputRef = ref<InstanceType<typeof DSBaseInput>>();
const { focus, blur } = useDSInput(passwordInputRef);

const dsFormField = useDSFormField<DSPasswordFieldModelValue>({
  name: computed(() => props.name),
  errorMessages: computed(() => props.errorMessages),
  value: modelValue,
  rules: computed(() => props.rules),
});

const { id } = useDSFormFieldId({ props });

const isPasswordVisible = ref(props.initiallyVisible);
const isFocused = ref(false);

const hasErrors = computed(() => !!dsFormField.errorMessages.value.length);
const isEditable = computed(() => !props.disabled && !props.readonly);
const isClearable = computed(() => isEditable.value && props.clearable);
const inputType = computed(() => (isPasswordVisible.value ? DS_BASE_INPUT_TYPE.Text : DS_BASE_INPUT_TYPE.Password));
const toggleIcon = computed(() => (isPasswordVisible.value ? "b-eye" : "eye"));
const toggleAriaLabel = computed(() => (isPasswordVisible.value ? HIDE_PASSWORD_LABEL : SHOW_PASSWORD_LABEL));
const { isCapsLockHelperTextVisible } = useDSPasswordFieldCapsLock({ isEditable, isFocused });
const helperText = computed(() => (isCapsLockHelperTextVisible.value ? CAPS_LOCK_WARNING_MESSAGE : props.helperText));

const rootClasses = computed(() => ({
  [DS_PASSWORD_FIELD_CLASSES.Root]: true,
  [DS_STATE_CLASSES.Disabled]: props.disabled,
  [DS_STATE_CLASSES.Error]: hasErrors.value,
}));

function onInput() {
  dsFormField.clearFieldErrors();
}

function onClear() {
  if (!isEditable.value) {
    return;
  }

  modelValue.value = "";
  dsFormField.clearFieldErrors();
}

function onFocus(event: FocusEvent) {
  isFocused.value = true;
  emit("focus", event);
}

function onBlur(event: FocusEvent) {
  isFocused.value = false;
  emit("blur", event);
}

function togglePasswordVisibility() {
  if (props.disabled) {
    return;
  }

  isPasswordVisible.value = !isPasswordVisible.value;
  focus();
}

defineExpose({
  focus,
  blur,
});
</script>
